import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import {
  OrulAuthProvider as OrulAuthSDKProvider,
  useOrulAuth,
  useTokens,
} from '@rhaimes/react'

import { Spinner } from 'core/components'
import { orulConfig } from 'core/configs/orul'
import { orulTokenManager } from 'core/services/orul-token-manager'
import {
  clearPendingSync,
  flushPendingSync,
  readPendingSync,
} from 'core/services/phone-sync-queue'

import { Actions } from 'store'

import { IAuthContext } from './types'

const AuthContext = createContext({} as IAuthContext)

/**
 * Provider interno que usa os hooks do Orul Auth e integra com Redux
 */
function AuthProviderInternal({ children }: any) {
  const {
    signIn: orulSignIn,
    signUp: orulSignUp,
    signOut: orulSignOut,
    isAuthenticated,
    isLoading: orulLoading,
    user: orulUser,
  } = useOrulAuth()
  const { tokens } = useTokens()
  const jaInicializou = useRef(false)

  const [loading, setLoading] = useState(true)
  const [isRedirecting, setIsRedirecting] = useState(false)

  const navigate = useNavigate()
  const dispatch = useDispatch()

  // Sincronizar tokens do Orul Auth com o OrulTokenManager
  useEffect(() => {
    if (tokens?.idToken) {
      orulTokenManager.setToken(tokens.idToken, tokens.expiresAt)

      // Reenvio no mesmo efeito, e nao num useEffect separado, de proposito: o
      // phone-sync viaja no axios `api`, que le o token deste manager. Em
      // efeitos separados a garantia seria a ordem de declaracao — inverter os
      // blocos faria a primeira tentativa sair sem Authorization, e isso falha
      // em silencio, virando so um retry no localStorage.
      //
      // O .catch e defensivo: hoje flushPendingSync trata todo erro
      // internamente e nunca rejeita.
      //
      // Sem `sub` nao da para saber de quem e a pendencia — em aparelho
      // compartilhado, enviar a de outro usuario renderia 403 e a apagaria.
      if (orulUser?.id) {
        flushPendingSync(orulUser.id).catch(() => {})
      }
    } else {
      orulTokenManager.clearToken()
    }
  }, [tokens?.idToken, tokens?.expiresAt, orulUser?.id])

  // Atualizar Redux store quando usuário do Orul Auth mudar
  useEffect(() => {
    if (orulUser && isAuthenticated) {
      // Mapear usuário do Orul Auth para formato do Redux
      dispatch(
        Actions.user.setData({
          attributes: {
            sub: orulUser.id,
            name: orulUser.name,
            phone_number: orulUser.phone || '',
            phone_number_verified: true, // Orul Auth já valida isso
            ...orulUser, // Isso inclui email e outros campos
          },
        }),
      )
    } else {
      // Limpar dados do usuário quando não autenticado
      dispatch(Actions.user.resetData())
    }
  }, [orulUser, isAuthenticated, dispatch])

  // Gerenciar loading state
  //
  // O espelhamento vale so ate a primeira carga terminar. Depois dela,
  // `orulLoading` volta a true a cada refreshSession — e como este provider
  // troca a arvore inteira por <Spinner/>, continuar espelhando desmontaria
  // tudo que estiver na tela, inclusive um modal com fluxo em andamento.
  useEffect(() => {
    if (isRedirecting || jaInicializou.current) {
      return
    }

    setLoading(orulLoading)

    if (!orulLoading) {
      jaInicializou.current = true
    }
  }, [orulLoading, isRedirecting])

  // Resetar isRedirecting quando usuário volta na navegação
  useEffect(() => {
    const handlePopState = () => {
      setIsRedirecting(false)
      setLoading(false)
    }

    // Escutar eventos de navegação (botão voltar/avançar)
    window.addEventListener('popstate', handlePopState)

    // Cleanup
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  const signOut = useCallback(async () => {
    // Lido antes do signOut: depois dele `orulUser` ja pode ter sido zerado.
    const subAtual = orulUser?.id

    try {
      await orulSignOut()

      localStorage.removeItem('username')

      // So a pendencia de quem esta saindo e descartada. A de outro usuario
      // continua valida para quando ele voltar a logar neste aparelho.
      if (subAtual && readPendingSync()?.sub === subAtual) {
        clearPendingSync()
      }

      // Redux será limpo pelo useEffect acima quando isAuthenticated mudar
      navigate('/', { replace: true })
    } catch (e) {
      // Erro ao fazer logout
    }
  }, [orulSignOut, navigate, orulUser?.id])

  const signIn = useCallback(
    async (orgKey?: string) => {
      setIsRedirecting(true)
      setLoading(true)

      // Timeout de segurança: se após 10 segundos não redirecionou, resetar
      const timeoutId = setTimeout(() => {
        setIsRedirecting(false)
        setLoading(false)
      }, 10000)

      try {
        // Usar modo redirect para Orul Hosted UI
        await orulSignIn({
          mode: 'redirect',
          state: 'login',
          callbackContext: orgKey ? `?loja=${orgKey}` : undefined,
        })
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : String(error)

        // Ignorar erro de "Redirecting..." pois é parte normal do fluxo
        if (errorMessage.includes('Redirecting')) {
          // Mantém isRedirecting=true e loading=true durante o redirect
          return
        }

        // Erro ao iniciar login
        clearTimeout(timeoutId)
        setIsRedirecting(false)
        setLoading(false)
      }
    },
    [orulSignIn],
  )

  const signUp = useCallback(
    async (orgKey?: string) => {
      setIsRedirecting(true)
      setLoading(true)

      // Timeout de segurança: se após 10 segundos não redirecionou, resetar
      const timeoutId = setTimeout(() => {
        setIsRedirecting(false)
        setLoading(false)
      }, 10000)

      try {
        // Usar modo redirect para Orul Hosted UI - tela de registro
        await orulSignUp({
          mode: 'redirect',
          state: 'signup',
          callbackContext: orgKey ? `?loja=${orgKey}` : undefined,
        })
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : String(error)

        // Ignorar erro de "Redirecting..." pois é parte normal do fluxo
        if (errorMessage.includes('Redirecting')) {
          // Mantém isRedirecting=true e loading=true durante o redirect
          return
        }

        // Erro ao iniciar cadastro
        clearTimeout(timeoutId)
        setIsRedirecting(false)
        setLoading(false)
      }
    },
    [orulSignUp],
  )

  const props = useMemo(
    () => ({ signIn, signUp, signOut, isAuthenticated, loading }),
    [isAuthenticated, signIn, signUp, signOut, loading],
  )

  return (
    <AuthContext.Provider value={props}>
      {loading ? <Spinner /> : children}
    </AuthContext.Provider>
  )
}

/**
 * Provider principal que envolve a aplicação com Orul Auth SDK
 */
function AuthProvider({ children }: any) {
  return (
    <OrulAuthSDKProvider config={orulConfig} autoRefresh>
      <AuthProviderInternal>{children}</AuthProviderInternal>
    </OrulAuthSDKProvider>
  )
}

export { AuthContext, AuthProvider }
